import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Empty,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  DatePicker,
} from 'antd';
import { ReloadOutlined, DownloadOutlined, ClearOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAdminSocketContext } from '../../sockets/AdminSocketContext';
import UserDetailsDrawer from './UserDetailsDrawer';
import {
  auditEventMeta,
  downloadCsv,
  formatDateTime,
  formatRelativeTime,
  todayFilename,
} from './adminFormatters';
import './adminLayout.sass';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const PAGE_SIZE = 50;

const AdminAudit = () => {
  const { listAudit, getFiltersMeta, getUser, onlineUserIds } = useAdminSocketContext();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [eventType, setEventType] = useState(null);
  const [userId, setUserId] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [payloadRow, setPayloadRow] = useState(null);
  const [profileUserId, setProfileUserId] = useState(null);

  useEffect(() => {
    getFiltersMeta()
      .then((res) => setMeta(res.meta))
      .catch(() => {});
  }, [getFiltersMeta]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listAudit({
        eventType: eventType || null,
        userId: userId ?? null,
        dateFrom: dateRange?.[0]?.startOf('day')?.toISOString() ?? null,
        dateTo: dateRange?.[1]?.endOf('day')?.toISOString() ?? null,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      setRows(res.rows || []);
      setTotal(res.total || 0);
    } catch (e) {
      message.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [listAudit, eventType, userId, dateRange, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Любое изменение фильтра возвращает на первую страницу, иначе можно
  // остаться на «пустой» странице после сужения выборки.
  const withPageReset = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const filtersActive = Boolean(eventType || userId || dateRange);

  const resetFilters = () => {
    setEventType(null);
    setUserId(null);
    setDateRange(null);
    setPage(1);
  };

  const handleExport = () => {
    downloadCsv(
      todayFilename('audit'),
      [
        { title: 'ID', dataIndex: 'id' },
        { title: 'Событие', value: (r) => auditEventMeta(r.event_type).label },
        { title: 'Тип события', dataIndex: 'event_type' },
        { title: 'Пользователь', value: (r) => r.user_username || r.user_id || '—' },
        { title: 'Инициатор', value: (r) => r.actor_username || r.actor_id || '—' },
        { title: 'IP', dataIndex: 'ip' },
        { title: 'Время', value: (r) => formatDateTime(r.created_at) },
        { title: 'Payload', value: (r) => (r.payload ? JSON.stringify(r.payload) : '') },
      ],
      rows
    );
    message.success(`Экспортировано событий: ${rows.length}`);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    {
      title: 'Событие',
      dataIndex: 'event_type',
      width: 190,
      render: (value) => {
        const event = auditEventMeta(value);
        return (
          <Tooltip title={value}>
            <Tag color={event.color}>{event.label}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Пользователь',
      dataIndex: 'user_username',
      width: 160,
      render: (value, row) =>
        row.user_id ? (
          <button
            type="button"
            className="admin-user-link"
            onClick={() => setProfileUserId(row.user_id)}
          >
            {value || `#${row.user_id}`}
          </button>
        ) : (
          value || '—'
        ),
    },
    {
      title: 'Инициатор',
      dataIndex: 'actor_username',
      width: 160,
      render: (value, row) =>
        row.actor_id ? (
          <button
            type="button"
            className="admin-user-link"
            onClick={() => setProfileUserId(row.actor_id)}
          >
            {value || `#${row.actor_id}`}
          </button>
        ) : (
          value || '—'
        ),
    },
    { title: 'IP', dataIndex: 'ip', width: 120, render: (v) => v || '—' },
    {
      title: 'Время',
      dataIndex: 'created_at',
      width: 160,
      render: (value) => (
        <Tooltip title={formatDateTime(value)}>
          <span>{formatRelativeTime(value)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Детали',
      dataIndex: 'payload',
      render: (payload) =>
        payload ? (
          <Button size="small" onClick={() => setPayloadRow(payload)}>
            Показать
          </Button>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <>
      <Card size="small" className="admin-filters-card">
        <Space wrap size="middle" align="start" className="admin-filters-card__space" style={{ width: '100%' }}>
          <Select
            allowClear
            showSearch
            placeholder="Пользователь"
            style={{ minWidth: 190 }}
            value={userId}
            onChange={withPageReset(setUserId)}
            optionFilterProp="label"
            options={(meta?.users || []).map((u) => ({
              value: u.id,
              label: `${u.username} (#${u.id})`,
            }))}
          />
          <Select
            allowClear
            placeholder="Тип события"
            style={{ minWidth: 220 }}
            value={eventType}
            onChange={withPageReset(setEventType)}
            optionFilterProp="label"
            options={(meta?.event_types || []).map((value) => ({
              value,
              label: auditEventMeta(value).label,
            }))}
          />
          <RangePicker
            value={dateRange}
            onChange={withPageReset(setDateRange)}
            format="DD.MM.YYYY"
            presets={[
              { label: 'Сегодня', value: [dayjs().startOf('day'), dayjs()] },
              { label: '7 дней', value: [dayjs().subtract(7, 'd'), dayjs()] },
              { label: '30 дней', value: [dayjs().subtract(30, 'd'), dayjs()] },
            ]}
          />
          <Button type="primary" icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Применить
          </Button>
          {filtersActive && (
            <Button icon={<ClearOutlined />} onClick={resetFilters}>
              Сбросить
            </Button>
          )}
          <Button icon={<DownloadOutlined />} onClick={handleExport} disabled={!rows.length}>
            CSV
          </Button>
        </Space>
      </Card>

      <Space size={12} wrap className="admin-list-meta">
        <span>Всего событий: {total}</span>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          showTotal: (count) => `Событий: ${count}`,
          onChange: setPage,
        }}
        scroll={{ x: 1200 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={filtersActive ? 'По фильтрам событий не найдено' : 'Событий пока нет'}
            />
          ),
        }}
      />

      <Modal
        title="Детали события"
        open={payloadRow != null}
        onCancel={() => setPayloadRow(null)}
        footer={null}
        width={Math.min(760, typeof window !== 'undefined' ? window.innerWidth - 24 : 760)}
      >
        {payloadRow != null && (
          <>
            <Space style={{ marginBottom: 8 }}>
              <Text copyable={{ text: JSON.stringify(payloadRow, null, 2) }}>Скопировать JSON</Text>
            </Space>
            <pre className="admin-json-view">{JSON.stringify(payloadRow, null, 2)}</pre>
          </>
        )}
      </Modal>

      <UserDetailsDrawer
        open={profileUserId != null}
        userId={profileUserId}
        onClose={() => setProfileUserId(null)}
        getUser={getUser}
        isOnline={(onlineUserIds || []).map(Number).includes(Number(profileUserId))}
      />
    </>
  );
};

export default AdminAudit;
